// http://dmcritchie.mvps.org/firefox/keyboard.htm

import { useEffect, useCallback } from "react"

export interface KeyBoardKey {
	/**
	 * Logical key
	 * https://developer.mozilla.org/de/docs/Web/API/KeyboardEvent/key/Key_Values
	 */
	key: string
	/**
	 * Equivalent to which, keyCode and chardCode
	 */
	legacyKey: number
}

export const keyValuesByKey = {
	"SHIFT D": {
		key: "D",
		legacyKey: 68,
	},
	"SHIFT +": {
		key: "*",
		legacyKey: 42,
	},
	"SHIFT -": {
		key: "_",
		legacyKey: 95,
	},
	"SHIFT ALT/OPTION D": {
		key: "™",
		legacyKey: 8482,
	},
	"SHIFT ALT/OPTION +": {
		key: "",
		legacyKey: 63743,
	},
	"SHIFT ALT/OPTION -": {
		key: "—",
		legacyKey: 8212,
	},
	"SHIFT ALT/OPTION E": {
		key: "‰",
		legacyKey: 8240,
	},
	"SHIFT ALT/OPTION G": {
		key: "Ì",
		legacyKey: 204,
	},
}

const getLegacyKey = (which: number, keyCode: number, charCode: number) => {
	// Handle possibility that key i a "0" (zero)
	return which || keyCode || charCode || 0
}

const isValidShortcutKey = (keyBoardKey: KeyBoardKey, key: string, legacyKey: number) => {
	return keyBoardKey.key === key || keyBoardKey.legacyKey === legacyKey
}

/**
 * A hook for creating keyboard shortcuts
 *
 * @param keyBoardKey Key that needs to be pressed for shortcut
 * @param callback The function to be invoked when
 */
const useKeyboardShortcut = (keyBoardKey: KeyBoardKey, callback: () => void) => {
	const handleKeyPress = useCallback(
		(e: KeyboardEvent) => {
			const { key, which, keyCode, charCode } = e

			const legacyKey = getLegacyKey(which, keyCode, charCode)

			if (isValidShortcutKey(keyBoardKey, key, legacyKey)) {
				callback()
			}
		},
		[keyBoardKey, callback],
	)

	useEffect(() => {
		addEventListener("keypress", handleKeyPress)

		return () => {
			removeEventListener("keypress", handleKeyPress)
		}
	}, [handleKeyPress, callback])
}

export default useKeyboardShortcut
